import { OnboardingDecisionChoice } from "@gamedev-web/one-gaming-crm";

import {
  AaaSelectionsData,
  BlockedReasons,
  CrmAaaEnablingStatusData,
  CrmAaaGameConceptStatusData,
  CrmApplicationStatus,
  CrmFastTrackStatusData,
  CrmGameConceptStatus,
  CrmGameConceptStatusDataBase,
  CrmGdkStatusData,
  CrmIdXboxGameConceptStatusData,
  CrmPitchConceptStatusData,
  CrmStatusDataBase,
  CurrentGameConcept,
  CurrentPitchConcept,
  SelectionsData,
} from "../../models/crmStatus";
import { IdXboxGameConceptData } from "../../models/forms";
import { Programs } from "../../models/programsConfig";
import { NdaStatus } from "../../models/status";
import {
  AaaGameConceptDataSet,
  FieldOption,
  GameConceptsQuery,
  GameConceptState,
  IdXboxGameConceptDataSet,
  PitchConceptDataSet,
} from "../../server/crm/api/models/forms";
import { OnboardingRootState } from "../reducers/rootReducer";
import { getIdXboxProgramCountriesOrRegions } from "./idXboxProgramSelectors";
import { getCurrentProgramData } from "./programsSelectors";
import { getOnboardingState } from "./rootSelectors";
import { getCurrentProgram } from "./statusSelectors";

const getCrmStatus = (state: OnboardingRootState) => {
  const programCrmStatus = getCurrentProgramData(state)?.crmStatus;
  if (programCrmStatus) {
    return programCrmStatus;
  }
  // Fall back to gameConceptPage selections when programs state isn't populated
  const gameConceptData = getOnboardingState(state)?.gameConceptPage?.data;
  return gameConceptData?.selections;
};

export const getCrmApplicationStatus = (
  state: OnboardingRootState
): CrmApplicationStatus | undefined =>
  (getCrmStatus(state) as CrmStatusDataBase)?.crmApplicationStatus;

export const getCurrentGameConcept = (state: OnboardingRootState): CurrentGameConcept | undefined =>
  (getCrmStatus(state) as CrmIdXboxGameConceptStatusData)?.currentGameConcept;

export const getIsAtLeastOneSignedGdkContract = (state: OnboardingRootState): boolean =>
  (getCrmStatus(state) as CrmGdkStatusData)?.isAtLeastOneSignedGdkContract;

export const getIsGdkReceived = (state: OnboardingRootState): boolean =>
  (getCrmStatus(state) as CrmGdkStatusData)?.isGdkReceived;

export const getBlockedReason = (state: OnboardingRootState): BlockedReasons =>
  (getCrmStatus(state) as CrmStatusDataBase)?.blockedReason;

export const getIsAaaOnboardingHubEnabled = (state: OnboardingRootState): boolean =>
  (getCrmStatus(state) as CrmAaaEnablingStatusData)?.isAaaOnboardingHubEnabled;

export const getIsFastTrackAccount = (state: OnboardingRootState): boolean =>
  (getCrmStatus(state) as CrmFastTrackStatusData)?.isFastTrackAccount;

export const getNdaSignedDate = (state: OnboardingRootState): Date | undefined =>
  (getCrmStatus(state) as CrmStatusDataBase)?.ndaSignedDate;

export const getNdaStatus = (state: OnboardingRootState): NdaStatus | undefined =>
  (getCrmStatus(state) as CrmStatusDataBase)?.ndaStatus;

export const getOnboardingDecision = (
  state: OnboardingRootState
): OnboardingDecisionChoice | undefined =>
  (getCrmStatus(state) as CrmStatusDataBase)?.onboardingDecision;

export const getGameConceptById = (
  state: OnboardingRootState,
  gameConceptId: string
): (IdXboxGameConceptData & GameConceptState) | undefined => {
  const gameConceptsList = (getCrmStatus(state) as CrmIdXboxGameConceptStatusData)
    ?.gameConceptsList;
  return gameConceptsList ? gameConceptsList[gameConceptId] : undefined;
};

export const getCountriesOrRegionsList = (state: OnboardingRootState): Array<FieldOption> =>
  (getCrmStatus(state) as SelectionsData)?.countriesOrRegions ??
  getIdXboxProgramCountriesOrRegions(state);

export const getGenres = (state: OnboardingRootState): Array<FieldOption> =>
  (getCrmStatus(state) as SelectionsData)?.genres;

export const getMiddlewareEngines = (state: OnboardingRootState): Array<FieldOption> =>
  (getCrmStatus(state) as SelectionsData)?.middlewareEngines;

export const getRatings = (state: OnboardingRootState): Array<FieldOption> =>
  (getCrmStatus(state) as SelectionsData)?.ratings;

export const getEsrbRatings = (state: OnboardingRootState): Array<FieldOption> =>
  (getCrmStatus(state) as AaaSelectionsData)?.worldwideRatings?.esrb;

export const getGermanyUskRatings = (state: OnboardingRootState): Array<FieldOption> =>
  (getCrmStatus(state) as AaaSelectionsData)?.worldwideRatings?.germanyUsk;

export const getTaiwanCsrrRatings = (state: OnboardingRootState): Array<FieldOption> =>
  (getCrmStatus(state) as AaaSelectionsData)?.worldwideRatings?.taiwanCsrr;

export const getEuropeIsfeRatings = (state: OnboardingRootState): Array<FieldOption> =>
  (getCrmStatus(state) as AaaSelectionsData)?.worldwideRatings?.europeIsfe;

export const getAustraliaAcbRatings = (state: OnboardingRootState): Array<FieldOption> =>
  (getCrmStatus(state) as AaaSelectionsData)?.worldwideRatings?.australiaAcb;

export const getRussiaPcbpRatings = (state: OnboardingRootState): Array<FieldOption> =>
  (getCrmStatus(state) as AaaSelectionsData)?.worldwideRatings?.russiaPcbp;

export const getJapanCeroRatings = (state: OnboardingRootState): Array<FieldOption> =>
  (getCrmStatus(state) as AaaSelectionsData)?.worldwideRatings?.japanCero;

export const getKoreaGrbRatings = (state: OnboardingRootState): Array<FieldOption> =>
  (getCrmStatus(state) as AaaSelectionsData)?.worldwideRatings?.koreaGrb;

export const getSaudiArabiaGamrRatings = (state: OnboardingRootState): Array<FieldOption> =>
  (getCrmStatus(state) as AaaSelectionsData)?.worldwideRatings?.saudiArabiaGamr;

export const getBrazilRatings = (state: OnboardingRootState): Array<FieldOption> =>
  (getCrmStatus(state) as AaaSelectionsData)?.worldwideRatings?.brazilRatings;

export const getNewZealandOflcRatings = (state: OnboardingRootState): Array<FieldOption> =>
  (getCrmStatus(state) as AaaSelectionsData)?.worldwideRatings?.newZealandOflc;

export const get3dSpatialSoundList = (state: OnboardingRootState): Array<FieldOption> =>
  (getCrmStatus(state) as AaaSelectionsData)?.spatial3dSoundList;

export const getGameConceptsList = (
  state: OnboardingRootState
): IdXboxGameConceptDataSet | AaaGameConceptDataSet => {
  const currentProgram = getCurrentProgram(state);
  switch (currentProgram) {
    case Programs.Aaa:
      return (getCrmStatus(state) as CrmAaaGameConceptStatusData)?.gameConceptsList;
    default:
      return (getCrmStatus(state) as CrmIdXboxGameConceptStatusData)?.gameConceptsList;
  }
};

export const getGameConceptsQuery = (state: OnboardingRootState): GameConceptsQuery | undefined =>
  (getCrmStatus(state) as CrmGameConceptStatusDataBase)?.gameConceptsListQuery;

export const getHasAtLeastOneAcceptedApplication = (state: OnboardingRootState): boolean =>
  (getCrmStatus(state) as CrmIdXboxGameConceptStatusData)?.hasAtLeastOneAcceptedApplication;

export const getHighestCrmGameConceptStatus = (
  state: OnboardingRootState
): CrmGameConceptStatus | undefined =>
  (getCrmStatus(state) as CrmIdXboxGameConceptStatusData)?.highestCrmGameConceptStatus;

// Pitch
export const getPitchConceptsList = (state: OnboardingRootState): PitchConceptDataSet =>
  (getCrmStatus(state) as CrmPitchConceptStatusData)?.pitchConceptLists;

export const getCurrentPitchConcept = (
  state: OnboardingRootState
): CurrentPitchConcept | undefined =>
  (getCrmStatus(state) as CrmPitchConceptStatusData)?.currentPitchConcept;
